import { useEffect, useRef } from 'react';

import { getNextItemIndex } from '../components/ui/use-menu-keyboard';

/**
 * Menu behavior for a `<details>` pull-down: closes when an item is chosen (focus returns to its
 * summary), on a click outside, on Tab, or on Escape (focus returns to its summary). Arrow keys
 * move between menu items.
 */
export function useDismissibleDetails() {
    const ref = useRef<HTMLDetailsElement>(null);

    useEffect(() => {
        const details = ref.current;
        if (!details) {
            return;
        }

        const close = (restoreFocus: boolean) => {
            if (!details.open) {
                return;
            }
            details.open = false;
            if (restoreFocus) {
                details.querySelector('summary')?.focus();
            }
        };
        const getItems = () =>
            Array.from(details.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])'));
        let frame = 0;
        const handleToggle = () => {
            if (details.open) {
                cancelAnimationFrame(frame);
                frame = requestAnimationFrame(() => getItems()[0]?.focus());
            }
        };
        const handlePointerDown = (event: PointerEvent) => {
            if (!details.contains(event.target as Node)) {
                close(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && details.open) {
                close(true);
            }
        };
        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('summary') && target.closest('a, button')) {
                // The chosen item is about to be hidden; keep focus on the visible menu button.
                close(true);
            }
        };
        const handleMenuKeyDown = (event: KeyboardEvent) => {
            if (!details.open || !(event.target as HTMLElement).closest('[role="menu"]')) return;
            if (event.key === 'Tab') {
                event.preventDefault();
                close(true);
                return;
            }
            const items = getItems();
            const index = items.indexOf(document.activeElement as HTMLElement);
            if (index < 0) return;
            const next = getNextItemIndex(event.key, index, items.length);
            if (next !== null) {
                event.preventDefault();
                items[next]?.focus();
            }
        };
        const handleFocusOut = (event: FocusEvent) => {
            const next = event.relatedTarget as Node | null;
            if (details.open && next && !details.contains(next)) close(false);
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        details.addEventListener('toggle', handleToggle);
        details.addEventListener('keydown', handleMenuKeyDown);
        details.addEventListener('focusout', handleFocusOut);
        details.addEventListener('click', handleClick);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
            details.removeEventListener('toggle', handleToggle);
            details.removeEventListener('keydown', handleMenuKeyDown);
            details.removeEventListener('focusout', handleFocusOut);
            details.removeEventListener('click', handleClick);
            cancelAnimationFrame(frame);
        };
    }, []);

    return ref;
}
